import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import "highlight.js/styles/github-dark.css";
import { getAgentEvents, sendThumbsFeedback } from "../../../Rest_api/RestApi";
import { AgentEvent } from "../../../models/agent";

interface MessagePart {
  text: string;
  function_call?: {
    id: string;
    args: any;
    name: string;
  };
}

interface MessageContent {
  role: string;
  parts: MessagePart[];
}

interface Message {
  id: string;
  app_name: string;
  user_id: string;
  session_id: string;
  invocation_id: string | null;
  author: string;
  branch: string | null;
  timestamp: string;
  content: MessageContent | string;
  actions?: string | null;
  long_running_tool_ids_json?: string | null;
  grounding_metadata?: any;
  partial?: boolean | null;
  turn_complete?: boolean | null;
  error_code?: string | null;
  error_message?: string | null;
  interrupted?: boolean | null;
  thumbs_up?: boolean | null;
}

const MAIN_AUTHORS = ["data_show", "gnai_ruvitl"];
const THINKING_AUTHORS = [
  "data_fetching_agent",
  "data_validation_agent",
  "plot_data_extractor",
  "PlotDataExtractor",
  "Checker",
];

const TIME_FILTERS = [
  { label: "All Time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
];

const markdownStyles = `
  .markdown-body {
    font-size: 14px;
    line-height: 1.5;
  }
  .markdown-body table {
    margin: 0.5em 0;
    border-collapse: separate;
    border-spacing: 0;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    width: 100%;
  }
  .markdown-body thead {
    background: rgba(0, 0, 0, 0.3);
    border-bottom: 2px solid rgba(255, 255, 255, 0.15);
  }
  .markdown-body thead th {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 1) !important;
    font-size: 13px;
    padding: 12px 16px;
    text-align: left;
    background: transparent !important;
  }
  .markdown-body tbody tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  .markdown-body tbody tr:last-child {
    border-bottom: none;
  }
  .markdown-body tbody tr:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  .markdown-body td {
    padding: 12px 16px;
    color: rgba(255, 255, 255, 0.8);
  }
  .markdown-body p {
    margin: 0.5em 0;
  }
  .markdown-body p:first-child {
    margin-top: 0;
  }
  .markdown-body p:last-child {
    margin-bottom: 0;
  }
  .markdown-body pre {
    margin: 0.5em 0;
  }
  .markdown-body pre:first-child {
    margin-top: 0;
  }
  .markdown-body pre:last-child {
    margin-bottom: 0;
  }
  .markdown-body ul, .markdown-body ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }
  .markdown-body ul:first-child, .markdown-body ol:first-child {
    margin-top: 0;
  }
  .markdown-body ul:last-child, .markdown-body ol:last-child {
    margin-bottom: 0;
  }
  .table-download-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 4px 8px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: white;
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s;
  }
  .table-download-btn:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  .markdown-table-wrap {
    position: relative;
  }
`;

export default function AgentHistory() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [selectedTime, setSelectedTime] = useState<string>("all");
  const [ratedMessages, setRatedMessages] = useState<
    Record<string, { isUp: boolean; isLoading: boolean }>
  >({});
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const events = await getAgentEvents();
        const formattedMessages = events
          .reverse()
          .filter((event) => {
            if (!event.content || !event.author) return false;
            const content = getMessageContent(event as Message);
            return content.trim().length > 0;
          })
          .map((event) => ({
            ...event,
            timestamp: new Date(event.timestamp).toLocaleString(),
          }));

        // Initialize ratedMessages with existing thumbs up/down values
        const initialRatedMessages: Record<
          string,
          { isUp: boolean; isLoading: boolean }
        > = {};
        events.forEach((event) => {
          if (event.invocation_id && event.thumbs_up !== undefined) {
            initialRatedMessages[event.invocation_id] = {
              isUp: event.thumbs_up === true,
              isLoading: false,
            };
          }
        });
        setRatedMessages(initialRatedMessages);

        setMessages(formattedMessages);
        setFilteredMessages(formattedMessages);
      } catch (error) {
        console.error("Error fetching history:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, []);

  useEffect(() => {
    let filtered = [...messages];

    // Filter by user
    if (selectedUser !== "all") {
      filtered = filtered.filter((msg) => msg.user_id === selectedUser);
    }

    // Filter by time
    if (selectedTime !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      filtered = filtered.filter((msg) => {
        const msgDate = new Date(msg.timestamp);
        switch (selectedTime) {
          case "today":
            return msgDate >= today;
          case "yesterday":
            return msgDate >= yesterday && msgDate < today;
          case "week":
            return msgDate >= weekAgo;
          case "month":
            return msgDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredMessages(filtered);
  }, [messages, selectedUser, selectedTime]);

  useEffect(() => {
    // Add the styles to the document head
    const styleElement = document.createElement("style");
    styleElement.innerHTML = markdownStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  /** Auto‑scroll on new messages */
  useEffect(() => {
    const scrollToBottom = () => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    };

    scrollToBottom();
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  const convertTableToCSV = (table: HTMLTableElement): string => {
    const rows = Array.from(table.querySelectorAll("tr"));
    return rows
      .map((row) => {
        const cells = Array.from(row.querySelectorAll("th, td"));
        return cells
          .map((cell) => {
            const text = cell.textContent?.trim() || "";
            return text.includes(",") ? `"${text.replace(/"/g, '""')}"` : text;
          })
          .join(",");
      })
      .join("\n");
  };

  const downloadTableAsCSV = (table: HTMLTableElement) => {
    const csv = convertTableToCSV(table);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFeedback = async (invocationId: string, thumbsUp: boolean) => {
    try {
      // Set loading state for this message
      setRatedMessages((prev) => ({
        ...prev,
        [invocationId]: { isUp: thumbsUp, isLoading: true },
      }));

      const result = await sendThumbsFeedback(invocationId, thumbsUp);
      if ("error" in result) {
        console.error("Failed to send feedback:", result.error);
        // Remove the rating on error
        setRatedMessages((prev) => {
          const newState = { ...prev };
          delete newState[invocationId];
          return newState;
        });
      } else {
        // Keep the selected state but remove loading
        setRatedMessages((prev) => ({
          ...prev,
          [invocationId]: { isUp: thumbsUp, isLoading: false },
        }));
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
      // Remove the rating on error
      setRatedMessages((prev) => {
        const newState = { ...prev };
        delete newState[invocationId];
        return newState;
      });
    }
  };

  const getMessageContent = (message: Message): string => {
    if (typeof message.content === "string") {
      return message.content;
    }
    if (
      message.content &&
      typeof message.content === "object" &&
      "parts" in message.content
    ) {
      return message.content.parts
        .map((part) => {
          if (part.function_call) {
            return `Function call: ${part.function_call.name}`;
          }
          return part.text;
        })
        .join("");
    }
    return "";
  };

  const getMessageRole = (message: Message): string => {
    if (
      typeof message.content === "object" &&
      message.content &&
      "role" in message.content
    ) {
      return message.content.role;
    }
    return message.author;
  };

  const uniqueUsers = Array.from(new Set(messages.map((msg) => msg.user_id)));

  return (
    <div className="flex-1 flex flex-col items-center justify-center w-full p-4 bg-[#f7fafc]">
      <div className="w-full h-[90vh] mx-auto bg-white rounded-3xl shadow-xl flex flex-col items-center">
        <div className="w-full px-8 pt-6 pb-4 border-b border-gray-100">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#34656d] focus:border-transparent"
            >
              <option value="all">All Users</option>
              {uniqueUsers.map((user) => (
                <option key={user} value={user}>
                  {user}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              {TIME_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setSelectedTime(filter.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedTime === filter.value
                      ? "bg-[#34656d] text-white"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <main className="flex-1 min-h-0 flex flex-col h-full items-center px-4 w-full">
          <div
            className="flex-1 min-h-0 w-full h-full px-8 pt-6 overflow-y-auto"
            style={{ scrollBehavior: "smooth" }}
            ref={listRef}
          >
            <div className="max-w-6xl mx-auto w-full space-y-6 pb-4">
              {isLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f1935c]"></div>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No messages found for the selected filters
                </div>
              ) : (
                filteredMessages.map((msg) => {
                  const role = getMessageRole(msg);
                  const content = getMessageContent(msg);
                  const isMain =
                    msg.author === "user" || MAIN_AUTHORS.includes(msg.author);
                  const isThinking = THINKING_AUTHORS.includes(msg.author);

                  return (
                    <div key={msg.id} className="w-full first:mt-2">
                      {isThinking ? (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                          <details className="group">
                            <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-gray-50 border-b border-gray-100">
                              <div className="flex items-center gap-2 text-[#34656d]">
                                <svg
                                  className="w-5 h-5 animate-spin"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                <span className="font-medium">Thinking...</span>
                              </div>
                              <span className="text-sm text-gray-500">
                                ({msg.author})
                              </span>
                              <svg
                                className="w-5 h-5 ml-auto transform transition-transform group-open:rotate-180"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </summary>
                            <div className="px-4 py-3 text-sm text-gray-600">
                              {content}
                            </div>
                          </details>
                        </div>
                      ) : (
                        <div
                          className={`flex ${
                            msg.author === "user"
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div
                            className={`inline-block px-6 py-4 rounded-2xl shadow-sm max-w-[85%] ${
                              msg.author === "user"
                                ? "bg-gradient-to-r from-[#f1935c] to-[#e67f45] text-white"
                                : "bg-[#34656d] text-white"
                            }`}
                          >
                            <div className="text-xs text-gray-300 mb-1 flex items-center gap-2">
                              <span>{msg.timestamp}</span>
                              <span>•</span>
                              <span>{msg.user_id}</span>
                              <span>•</span>
                              <span>{msg.author}</span>
                            </div>
                            {msg.author === "user" ? (
                              <div
                                className="whitespace-pre-wrap"
                                style={{ fontFamily: "inherit" }}
                              >
                                {content}
                              </div>
                            ) : (
                              <div className="relative">
                                <div
                                  className="markdown-body prose prose-sm max-w-none prose-invert"
                                  style={{ fontFamily: "inherit" }}
                                >
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                                    components={{
                                      table: ({
                                        children,
                                        ...props
                                      }: React.HTMLProps<HTMLTableElement>) => (
                                        <div
                                          className="markdown-table-wrap"
                                          style={{
                                            position: "relative",
                                            marginBottom: "1.5rem",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "flex-end",
                                              marginBottom: "0.5rem",
                                            }}
                                          >
                                            <button
                                              className="table-download-btn"
                                              style={{
                                                position: "static",
                                                zIndex: 10,
                                              }}
                                              onClick={(e) => {
                                                const container =
                                                  e.currentTarget.closest(
                                                    ".markdown-table-wrap"
                                                  );
                                                const table =
                                                  container?.querySelector(
                                                    "table"
                                                  );
                                                if (table)
                                                  downloadTableAsCSV(
                                                    table as HTMLTableElement
                                                  );
                                              }}
                                            >
                                              <svg
                                                className="w-4 h-4"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                              >
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line
                                                  x1="12"
                                                  y1="15"
                                                  x2="12"
                                                  y2="3"
                                                />
                                              </svg>
                                              Download CSV
                                            </button>
                                          </div>
                                          <div className="overflow-x-auto">
                                            <table
                                              className="min-w-full"
                                              {...props}
                                            >
                                              {children}
                                            </table>
                                          </div>
                                        </div>
                                      ),
                                      thead: ({
                                        children,
                                        ...props
                                      }: React.HTMLProps<HTMLTableSectionElement>) => (
                                        <thead {...props}>{children}</thead>
                                      ),
                                      th: ({
                                        children,
                                        ...props
                                      }: React.HTMLProps<HTMLTableHeaderCellElement>) => (
                                        <th
                                          className="px-4 py-3 text-left text-sm font-semibold text-white/90"
                                          {...props}
                                        >
                                          {children}
                                        </th>
                                      ),
                                      td: ({
                                        children,
                                        ...props
                                      }: React.HTMLProps<HTMLTableDataCellElement>) => (
                                        <td
                                          className="px-4 py-3 text-sm whitespace-normal text-white/80"
                                          {...props}
                                        >
                                          {children}
                                        </td>
                                      ),
                                      tr: ({
                                        children,
                                        ...props
                                      }: React.HTMLProps<HTMLTableRowElement>) => (
                                        <tr
                                          className="transition-colors duration-150"
                                          {...props}
                                        >
                                          {children}
                                        </tr>
                                      ),
                                      code: ({
                                        inline,
                                        className,
                                        children,
                                        ...props
                                      }: any) => {
                                        return inline ? (
                                          <code
                                            className="px-1.5 py-0.5 rounded-md bg-gray-700/50 text-gray-100 text-sm"
                                            {...props}
                                          >
                                            {children}
                                          </code>
                                        ) : (
                                          <div className="rounded-md bg-gray-800/50 p-4">
                                            <code
                                              className="text-gray-100 text-sm"
                                              {...props}
                                            >
                                              {children}
                                            </code>
                                          </div>
                                        );
                                      },
                                      p: ({ children }) => (
                                        <p className="markdown-paragraph">
                                          {children}
                                        </p>
                                      ),
                                    }}
                                  >
                                    {content}
                                  </ReactMarkdown>
                                </div>
                                {msg.invocation_id && (
                                  <div className="flex gap-2 mt-2 justify-end h-8">
                                    {ratedMessages[msg.invocation_id]
                                      ?.isLoading ? (
                                      <div className="flex items-center justify-center w-8 h-8">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      </div>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() =>
                                            handleFeedback(
                                              msg.invocation_id!,
                                              true
                                            )
                                          }
                                          className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                                            ratedMessages[msg.invocation_id]
                                              ?.isUp
                                              ? "bg-white/20"
                                              : "hover:bg-white/10"
                                          }`}
                                          title="Thumbs up"
                                        >
                                          <svg
                                            className={`w-4 h-4 ${
                                              ratedMessages[msg.invocation_id]
                                                ?.isUp
                                                ? "text-green-400"
                                                : ""
                                            }`}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleFeedback(
                                              msg.invocation_id!,
                                              false
                                            )
                                          }
                                          className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                                            ratedMessages[msg.invocation_id]
                                              ?.isUp === false
                                              ? "bg-white/20"
                                              : "hover:bg-white/10"
                                          }`}
                                          title="Thumbs down"
                                        >
                                          <svg
                                            className={`w-4 h-4 ${
                                              ratedMessages[msg.invocation_id]
                                                ?.isUp === false
                                                ? "text-red-400"
                                                : ""
                                            }`}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zM17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
                                          </svg>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
